import * as PropTypes from "prop-types";
import React, {useEffect} from "react";
import {BETREIBER, FACHADMIN, SYSTEMADMIN, ZUST_SBD} from "../../../consts/funktionaleRollen";
import {userHasAnyRole} from "../../../utils/SecurityUtils";
import {LageVerortung} from "../common/LageVerortung";
import {AnlagenHeader} from "./AnlagenHeader";
import {ENTLASTUNG} from "../../../consts/anlagentyp";
import AnlagenFooter from "./AnlagenFooter";
import {useAnlagenstatus} from "../../../services/AnlagenService";
import EntlastungstypSelector from "./EntlastungstypSelector";
import {useHttp} from "../../../services/HttpService";
import {getDigiWabUrlEndpoint, getWasserrechtEndpoint} from "../../../services/BescheidsService";
import {EntlastungsanlagenEinleiter} from "./EntlastungsanlagenEinleiter";
import {useLocation} from "react-router-dom";
import routes from "../../../consts/routes.json";
import {getSuevoaAnlageNrEndpoint} from "../../../services/WertelistenService";

export default function Entlastung(props) {
    const [anlagenstatusOptionen] = useAnlagenstatus(props.data?.anlagenart?.id, props.data?.anlagentyp?.id, props.betriebstyp?.id);
    const [digiWabUrl, fetchDigiWabUrl] = useHttp(getDigiWabUrlEndpoint(), {manual: true});
    const [wasserrecht, fetchWasserrecht] = useHttp(getWasserrechtEndpoint(props.data.wasserbuchnummer), {manual: true});
    const [suevoaAnlageNr] = useHttp(getSuevoaAnlageNrEndpoint);
    const location = useLocation();

    useEffect(() => {
        if (!digiWabUrl.loading && !digiWabUrl.error && !digiWabUrl.data &&
            !wasserrecht.loading && !wasserrecht.error && !wasserrecht.data) {
            fetchWasserrecht().catch(() => {
                // Do nothing
            });
            fetchDigiWabUrl().catch(() => {
                // Do nothing
            })
        }
    }, [digiWabUrl.loading, digiWabUrl.error, digiWabUrl.data, fetchDigiWabUrl, wasserrecht.loading, wasserrecht.error, wasserrecht.data, fetchWasserrecht]);

    function isDisabled() {
        if (!props.anlageId) return false;
        if (props.modus === "l") {
            return true;
        }
        if (props.modus === "s") {
            return !userHasAnyRole([ZUST_SBD, FACHADMIN, SYSTEMADMIN]);
        }
        return true;
    }

    function isLageVerortungDisabled() {
        if (!props.anlageId) return false;
        if (props.modus === "s") {
            return !userHasAnyRole([ZUST_SBD, FACHADMIN, SYSTEMADMIN]);
        }
        return true;
    }

    function getWasserrechtLink() {
        if (wasserrecht.data) {
            return <div>
                <text style={{fontWeight: "bold"}}>{"WBB-Nr.: "}</text>
                <br/>
                <a
                    target="_blank"
                    href={calculateDigiwabLink()}
                    rel="noopener noreferrer"
                >
                    {props.data.wasserbuchnummer}
                </a>
            </div>
        }
        return <div></div>
    }

    function calculateDigiwabLink() {
        return location.pathname.replaceAll(/betriebsakte.*$/g, routes.WASSERBUCH) + '/wbb' + wasserrecht.data.id + '?wbbId=' + wasserrecht.data.id;
    }

    function getWasserbuchnummer() {
        if (!isDisabled() || !wasserrecht.data) {
            return props.formFactory.renderInput("wasserbuchnummer", "wasserbuchnummer", "WBB-Nr.");
        }
        return getWasserrechtLink();
    }

    return (
        <div className="gridl tabsheet-content-margin">
            <AnlagenHeader
                withAnlagenart
                formFactory={props.formFactory}
                disabled={isDisabled() && !userHasAnyRole([BETREIBER])}
                statusDisabled={!props.anlageId || isDisabled()}
                anlagenstatusOptionen={anlagenstatusOptionen.data}
                angeschlosseneAnlage={props.data?.angeschlosseneAnlage}
                anlagenstatus={props.data?.anlagenstatus}
                isEntlastung={true}
            />
            <EntlastungsanlagenEinleiter
                formFactory={props.formFactory}
                betriebsId={props.betriebsId}
                anlageId={props.anlageId}
                anlagentyp={ENTLASTUNG}
                anlagenart={props.data?.anlagenart?.id}
                onChange={props.onChange}
                isButtonVisible={userHasAnyRole([ZUST_SBD, FACHADMIN, SYSTEMADMIN, BETREIBER]) || (userHasAnyRole([BETREIBER]) && !props.anlageId)}
                disabled={false}
            />
            <LageVerortung
                formFactory={props.formFactory}
                disabled={isLageVerortungDisabled() && !userHasAnyRole([BETREIBER])}
                data={props.data}
                onClick={(lage) => props.onChange("lage", lage)}
                isButtonVisible={userHasAnyRole([ZUST_SBD, FACHADMIN, SYSTEMADMIN, BETREIBER])}
                modus={props.modus}
            />
            <div className="gridl" style={{gridTemplateColumns: "repeat(3,max-content) 1fr 1fr"}}>
                {props.formFactory.renderCheckbox("tegAbschluss", "tegAbschluss", "TEG Abschluss", isDisabled() && !userHasAnyRole([BETREIBER]))}
                {props.formFactory.renderCheckbox("aufKAGelaende", "aufKAGelaende", "Auf KA-Gelände", isDisabled() && !userHasAnyRole([BETREIBER]))}
                {props.formFactory.renderCheckbox(
                    "selbstueberwachungspflicht",
                    "selbstueberwachungspflicht",
                    "Selbstüberwachungspflicht",
                    isDisabled()
                )}
                <div style={{ gridColumn: "1 / -1" }}>
                    {props.formFactory.renderAutocomplete(
                        "suevoaAnlageNr",
                        "suevoaAnlageNr",
                        "SÜVOA Anlage Nr.",
                        suevoaAnlageNr.data,
                        "value",
                        "key",
                        suevoaAnlageNr.loading,
                        false,
                        isDisabled(),
                        {
                            fullWidth: true,
                            style: { width: "100%", minWidth: "60ch" },
                        }
                    )}
                </div>
            </div>
            <div className="gridl" style={{gridTemplateColumns: "auto 7em 12em 12em"}}>
                {props.formFactory.renderInput("gemarkung", "gemarkung", "Gemarkung")}
                {props.formFactory.renderInput("flur", "flur", "Flur")}
                {props.formFactory.renderInput("flurstueck", "flurstueck", "Flurstück")}
                {getWasserbuchnummer()}
            </div>
            <EntlastungstypSelector formFactory={props.formFactory} isDisabled={isDisabled}/>
            <AnlagenFooter
                formFactory={props.formFactory}
                withAnlagenart={true}
                withDwaAndStaLa={false}
                isDisabled={isDisabled()}/>
        </div>
    );
}

Entlastung.propTypes = {
    anlageId: PropTypes.number,
    modus: PropTypes.oneOf(["l", "s"]).isRequired,
};
